
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface CourseProgress {
  id: string;
  course_id: string;
  last_lesson_id: string;
  updated_at: string;
  courses?: {
    title: string;
    thumbnail_url: string | null;
  };
  lessons?: {
    title: string;
    module_id: string;
  };
  modules?: {
    title: string;
  };
}

export const useCourseProgress = () => {
  const [loading, setLoading] = useState(true);
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);

  const fetchCourseProgress = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data, error } = await supabase
        .from('user_course_progress')
        .select(`
          *,
          courses (
            title,
            thumbnail_url
          ),
          lessons:last_lesson_id (
            title,
            module_id
          )
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching course progress:", error);
        return;
      }
      
      setCourseProgress(data);
    } catch (error) {
      console.error("Error in course progress fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateCourseProgress = async (courseId: string, lessonId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      // Check if progress exists for this course
      const { data: existingProgress } = await supabase
        .from('user_course_progress')
        .select('id')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .maybeSingle();
      
      if (existingProgress) {
        // Update existing progress
        const { error } = await supabase
          .from('user_course_progress')
          .update({
            last_lesson_id: lessonId,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingProgress.id);
        
        if (error) {
          console.error("Error updating course progress:", error);
          return null;
        }
      } else {
        // Create new progress entry
        const { error } = await supabase
          .from('user_course_progress')
          .insert({
            user_id: user.id,
            course_id: courseId,
            last_lesson_id: lessonId
          });
        
        if (error) {
          console.error("Error creating course progress:", error);
          return null;
        }
      }
      
      // Refetch to update state
      fetchCourseProgress();
      return true;
    } catch (error) {
      console.error("Error tracking course progress:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchCourseProgress();
  }, []);

  return {
    courseProgress,
    loading,
    updateCourseProgress,
    fetchCourseProgress
  };
};
